import type {APIHelper, DeploymentEvent, GitInfo} from '../interfaces'
import type {RequestError} from '@datadog/datadog-ci-base/helpers/request'

import {DoraDeploymentCommand} from '@datadog/datadog-ci-base/commands/dora/deployment'
import {FIPS_ENV_VAR, FIPS_IGNORE_ERROR_ENV_VAR} from '@datadog/datadog-ci-base/constants'
import {toBoolean} from '@datadog/datadog-ci-base/helpers/env'
import {enableFips} from '@datadog/datadog-ci-base/helpers/fips'
import {gitRepositoryURL, gitHash} from '@datadog/datadog-ci-base/helpers/git/get-git-data'
import {Logger, LogLevel} from '@datadog/datadog-ci-base/helpers/logger'
import {retryRequest} from '@datadog/datadog-ci-base/helpers/retry'
import chalk from 'chalk'
import simpleGit from 'simple-git'

import {apiConstructor} from '../api'
import {
  renderDryRun,
  renderFailedRequest,
  renderGitWarning,
  renderRequest,
  renderRetriedRequest,
  renderSuccessfulRequest,
} from '../renderer'

const nonRetriableErrorCodes = [400, 403]

export class PluginCommand extends DoraDeploymentCommand {
  private config = {
    apiKey: process.env.DATADOG_API_KEY || process.env.DD_API_KEY,
    fips: toBoolean(process.env[FIPS_ENV_VAR]) ?? false,
    fipsIgnoreError: toBoolean(process.env[FIPS_IGNORE_ERROR_ENV_VAR]) ?? false,
  }

  private logger: Logger = new Logger((s: string) => this.context.stdout.write(s), LogLevel.INFO)

  private service!: string
  private gitInfo?: GitInfo

  public async execute() {
    enableFips(this.fips || this.config.fips, this.fipsIgnoreError || this.config.fipsIgnoreError)

    this.logger.setLogLevel(this.verbose ? LogLevel.DEBUG : LogLevel.INFO)
    this.logger.setShouldIncludeTime(this.verbose)

    if (this.serviceParam) {
      this.service = this.serviceParam
    } else {
      this.logger.error('Missing service. It must be provided with --service or the DD_SERVICE env var')

      return 1
    }

    if (this.startedAt > (this.finishedAt || new Date())) {
      this.logger.error('--started-at cannot be after --finished-at')

      return 1
    }

    if (this.skipGit) {
      this.gitInfo = undefined
    } else if (this.gitRepoURL && this.gitCommitSHA) {
      this.gitInfo = {repoURL: this.gitRepoURL, commitSHA: this.gitCommitSHA}
    } else {
      this.gitInfo = await this.getGitInfo()
      this.logger.warn(renderGitWarning(this.gitInfo))
    }

    const api = this.getApiHelper()
    await this.sendDeploymentEvent(api, this.buildDeploymentEvent())

    if (!this.dryRun) {
      this.logger.info(renderSuccessfulRequest(this.service))
    }
  }

  private async getGitInfo(): Promise<GitInfo> {
    const git = simpleGit({
      baseDir: process.cwd(),
      binary: 'git',
      // We are invoking at most 5 git commands at the same time.
      maxConcurrentProcesses: 5,
    })
    const [repoURL, commitSHA] = await Promise.all([gitRepositoryURL(git), gitHash(git)])

    return {repoURL, commitSHA}
  }

  private getApiHelper(): APIHelper {
    if (!this.config.apiKey) {
      this.logger.error(
        `Neither ${chalk.red.bold('DATADOG_API_KEY')} nor ${chalk.red.bold('DD_API_KEY')} is in your environment.`
      )
      throw new Error('API key is missing')
    }

    return apiConstructor(this.config.apiKey)
  }

  private buildDeploymentEvent(): DeploymentEvent {
    const deployment: DeploymentEvent = {
      service: this.service,
      startedAt: this.startedAt,
      finishedAt: this.finishedAt || new Date(),
    }
    if (this.env) {
      deployment.env = this.env
    }
    if (this.gitInfo) {
      deployment.git = this.gitInfo
    }
    if (this.version) {
      deployment.version = this.version
    }
    if (this.team) {
      deployment.team = this.team
    }
    if (this.customTags) {
      deployment.customTags = this.customTags
    }

    return deployment
  }

  private async sendDeploymentEvent(api: APIHelper, deployment: DeploymentEvent) {
    if (this.dryRun) {
      this.logger.info(renderDryRun(deployment))

      return
    }

    try {
      this.logger.info(renderRequest(this.service))
      await retryRequest(() => api.sendDeploymentEvent(deployment), {
        onRetry: (e, attempt) => {
          this.logger.warn(renderRetriedRequest(this.service, e, attempt))
        },
        retries: 5,
      })
    } catch (error) {
      this.logger.error(renderFailedRequest(this.service, error as RequestError))
      if (error.response) {
        if (!nonRetriableErrorCodes.includes(error.response.status)) {
          return
        }
      }
      throw error
    }
  }
}
